// ─────────────────────────────────────────────────────────────
// AttachmentService — 배송 요청별 작업사진 ZIP 첨부 관리
// v7.177: Supabase Storage → 자체 nginx WebDAV로 전환
//          이유: Supabase Storage 파일 크기 제한 회피 + QNAP NAS 무제한 활용
//
// 서버 구성 (nginx.conf):
//   location /attachments/ {
//     alias /usr/share/nginx/attachments/;
//     dav_methods PUT DELETE;
//     create_full_put_path on;
//     client_max_body_size 2G;
//     autoindex on;
//   }
//
// 파일 저장 경로 (호스트 기준):
//   ./attachments/tx_<txId>/<timestamp>_<safename>.zip
// ─────────────────────────────────────────────────────────────

const PATH_PREFIX = '/attachments';

// 현재 도메인 기반 URL 생성 (Cloudflare Tunnel / 내부 IP 모두 대응)
function baseUrl() {
  if (typeof window === 'undefined') return '';
  return window.location.origin;
}

export const AttachmentService = {
  // 파일 업로드 (PUT) → { path, name, size, uploadedAt } 반환
  async upload(txId, file, uploadedBy) {
    if (!file) return { ok: false, error: 'no file' };
    const ts = Date.now();
    const safeName = (file.name || 'upload.zip').replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `tx_${txId}/${ts}_${safeName}`;
    const url = `${baseUrl()}${PATH_PREFIX}/${path}`;
    try {
      const res = await fetch(url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type || 'application/zip',
        },
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        return { ok: false, error: { message: `HTTP ${res.status} ${res.statusText}${txt ? ': ' + txt.slice(0, 200) : ''}` } };
      }
      return {
        ok: true,
        data: {
          path,
          name: file.name || safeName,
          size: file.size,
          uploadedAt: new Date().toISOString(),
          uploadedBy: uploadedBy || null,
        },
      };
    } catch (err) {
      return { ok: false, error: { message: err.message || String(err) } };
    }
  },

  // v7.312: 임의 디렉터리 업로드 — 메모 이미지(memo_<id>/) / 작업파일 공유(shared/)
  //   upload() 는 tx_<txId> 로 경로가 고정돼 있어 재사용이 안 된다. 그쪽은 건드리지 않는다.
  //
  //   ⚠ 파일명 처리: safeName 정규식이 non-ASCII 를 전부 '_' 로 바꾸기 때문에
  //     한글 파일명("작업지시서.pdf")은 통째로 "_____.pdf" 가 되어 서로 충돌한다.
  //     → 경로에는 timestamp + 랜덤을 넣어 유일성을 확보하고,
  //       사람이 볼 원본 이름은 호출자가 DB(shared_files.name / memos.images[].name)에 따로 저장한다.
  async uploadToDir(dir, file, uploadedBy) {
    if (!file) return { ok: false, error: { message: 'no file' } };
    const raw = file.name || 'upload.bin';
    const extM = raw.match(/\.[a-zA-Z0-9]{1,8}$/);
    const ext = extM ? extM[0].toLowerCase() : '';
    const base = (ext ? raw.slice(0, -ext.length) : raw)
      .replace(/[^a-zA-Z0-9._-]/g, '_')
      .replace(/_+/g, '_')
      .slice(0, 40) || 'file';
    const ts = Date.now();
    const rand = Math.random().toString(36).slice(2, 8);
    const path = `${dir}/${ts}_${rand}_${base}${ext}`;
    const url = `${baseUrl()}${PATH_PREFIX}/${path}`;
    try {
      const res = await fetch(url, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        return { ok: false, error: { message: `HTTP ${res.status} ${res.statusText}${txt ? ': ' + txt.slice(0, 200) : ''}` } };
      }
      return {
        ok: true,
        data: {
          path,
          name: raw,
          size: file.size,
          mime: file.type || '',
          uploadedAt: new Date().toISOString(),
          uploadedBy: uploadedBy || null,
        },
      };
    } catch (err) {
      return { ok: false, error: { message: err.message || String(err) } };
    }
  },

  // 파일 삭제 (DELETE)
  async remove(path) {
    if (!path) return { ok: false, error: 'no path' };
    const url = `${baseUrl()}${PATH_PREFIX}/${path}`;
    try {
      const res = await fetch(url, { method: 'DELETE' });
      // 404 (이미 없음)는 성공으로 간주
      if (!res.ok && res.status !== 404) {
        return { ok: false, error: { message: `HTTP ${res.status}` } };
      }
      return { ok: true };
    } catch (err) {
      return { ok: false, error: { message: err.message || String(err) } };
    }
  },

  // 다운로드 URL (GET) — 브라우저가 인증 없이 접근 가능
  getPublicUrl(path) {
    if (!path) return null;
    return `${baseUrl()}${PATH_PREFIX}/${path}`;
  },

  // 호환성 유지 — signed URL 개념 없음, 그냥 public URL 반환
  async getSignedUrl(path) {
    return this.getPublicUrl(path);
  },
};
